import React from 'react';
import { AlertTriangle, MessageCircleOff, VolumeX, MessageSquareWarning } from 'lucide-react';

/**
 * R.I.S.C — Rolled out October 1, 2026. Quick-scan for at-risk team
 * members. Four fixed categories, each with Who / What / Action Taken
 * free-text. Data starts blank each month (no rollover of prior data).
 */
export const RISC_ERA_YEAR = 2026;
export const RISC_ERA_MONTH = 10;
export const isRiscEra = (month, year) =>
  Number(year) > RISC_ERA_YEAR ||
  (Number(year) === RISC_ERA_YEAR && Number(month) >= RISC_ERA_MONTH);

const CATEGORIES = [
  {
    key: 'revolving_door',
    letter: 'R',
    title: 'Revolving Door',
    blurb: 'Any change; ownership, key personnel, regulations that impact reimbursement, contracts, behavior',
    Icon: AlertTriangle,
    accent: 'from-amber-500 to-orange-500',
    ring: 'ring-amber-100 border-amber-200',
  },
  {
    key: 'indifference',
    letter: 'I',
    title: 'Indifference',
    blurb: 'Lack of interest or concern, things are "okay", don\'t attend meetings',
    Icon: MessageCircleOff,
    accent: 'from-sky-500 to-cyan-500',
    ring: 'ring-sky-100 border-sky-200',
  },
  {
    key: 'silence',
    letter: 'S',
    title: 'Silence',
    blurb: "Quiet, don't know what they are thinking (good or bad), difficult to read",
    Icon: VolumeX,
    accent: 'from-slate-500 to-slate-700',
    ring: 'ring-slate-100 border-slate-200',
  },
  {
    key: 'communications',
    letter: 'C',
    title: 'Communications',
    blurb: 'Repeated communication breakdowns that are starting to affect trust, teamwork, or relationships',
    Icon: MessageSquareWarning,
    accent: 'from-rose-500 to-pink-500',
    ring: 'ring-rose-100 border-rose-200',
  },
];

const cellCls =
  'w-full text-sm px-3 py-2 rounded-md border border-slate-200 bg-white focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200 resize-y min-h-[72px]';

export const RiscSection = ({ risc, onChange, readOnly = false }) => {
  const data = risc || {};

  const update = (categoryKey, field, value) => {
    if (readOnly) return;
    const prev = data[categoryKey] || { who: '', what: '', action_taken: '' };
    onChange?.({
      ...data,
      [categoryKey]: { ...prev, [field]: value },
    });
  };

  return (
    <section className="mb-8" data-testid="risc-section">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-lg font-bold text-slate-900 tracking-tight">R.I.S.C.</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
          At-risk radar
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Quick scan for team members showing warning signals — capture the situation and the action taken.
      </p>

      <div className="space-y-4">
        {CATEGORIES.map(({ key, letter, title, blurb, Icon, accent, ring }) => {
          const item = data[key] || { who: '', what: '', action_taken: '' };
          return (
            <div
              key={key}
              data-testid={`risc-row-${key}`}
              className={`rounded-xl border bg-slate-50/40 p-4 ring-1 ${ring}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${accent} text-white flex items-center justify-center font-bold text-lg shadow-sm`}>
                  {letter}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <div className="text-sm font-bold text-slate-900">{title}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{blurb}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Who</label>
                  <textarea
                    className={cellCls}
                    placeholder="Name(s) or role(s)…"
                    value={item.who || ''}
                    onChange={(e) => update(key, 'who', e.target.value)}
                    disabled={readOnly}
                    data-testid={`risc-${key}-who`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">What</label>
                  <textarea
                    className={cellCls}
                    placeholder="What are you observing?"
                    value={item.what || ''}
                    onChange={(e) => update(key, 'what', e.target.value)}
                    disabled={readOnly}
                    data-testid={`risc-${key}-what`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Action Taken</label>
                  <textarea
                    className={cellCls}
                    placeholder="What have you done about it?"
                    value={item.action_taken || ''}
                    onChange={(e) => update(key, 'action_taken', e.target.value)}
                    disabled={readOnly}
                    data-testid={`risc-${key}-action`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default RiscSection;
